import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api, formatMoney } from "../lib/api";
import { useApp } from "../context/AppContext";
import { LogOut, Package, User, Mail, Calendar } from "lucide-react";

const MyAccount = () => {
  const { lang, currency, customer, logoutCustomer } = useApp();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!customer) return;
    api.get("/customer/orders")
      .then((r) => setOrders(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [customer]);

  if (!customer) return <Navigate to="/" replace />;

  const handleLogout = async () => {
    await logoutCustomer();
    navigate("/", { replace: true });
  };

  const statusColor = (st) => ({
    pending: "bg-holy-gold/15 text-holy-gold-dark",
    processing: "bg-blue-100 text-blue-700",
    shipped: "bg-purple-100 text-purple-700",
    delivered: "bg-holy-green/15 text-holy-green",
    cancelled: "bg-red-100 text-red-700",
  }[st] || "bg-cream text-foreground");

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
      <div className="text-center mb-10">
        <div className="text-[11px] tracking-[0.3em] uppercase text-holy-gold-dark font-semibold mb-3">✦ {lang === "id" ? "Akun Saya" : "My Account"} ✦</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-holy-red">{lang === "id" ? "Selamat Datang" : "Welcome back"}, {customer.name?.split(" ")[0] || "Friend"}</h1>
      </div>

      <div className="bg-white rounded-2xl border border-border/60 p-6 mb-6 flex items-start gap-4">
        {customer.picture ? (
          <img src={customer.picture} alt="" className="w-16 h-16 rounded-full object-cover ring-2 ring-holy-red/20" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-cream flex items-center justify-center"><User className="w-7 h-7 text-holy-red" /></div>
        )}
        <div className="flex-1">
          <div className="font-serif text-xl">{customer.name}</div>
          <div className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5"><Mail className="w-3.5 h-3.5" />{customer.email}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1"><Calendar className="w-3 h-3" />{lang === "id" ? "Bergabung" : "Joined"} {new Date(customer.created_at).toLocaleDateString(lang === "id" ? "id-ID" : "en-US")}</div>
        </div>
        <button onClick={handleLogout} data-testid="customer-logout" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border hover:border-holy-red hover:text-holy-red text-sm">
          <LogOut className="w-4 h-4" /> {lang === "id" ? "Keluar" : "Sign Out"}
        </button>
      </div>

      <h2 className="font-serif text-2xl text-foreground mb-4 flex items-center gap-2"><Package className="w-5 h-5 text-holy-red" /> {lang === "id" ? "Riwayat Pesanan" : "Order History"}</h2>
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : orders.length === 0 ? (
        <div className="bg-cream rounded-2xl p-8 text-center" data-testid="no-orders">
          <p className="text-muted-foreground mb-4">{lang === "id" ? "Belum ada pesanan." : "No orders yet."}</p>
          <Link to="/shop" className="inline-flex bg-holy-red text-ivory rounded-full px-6 py-2.5 text-sm">{lang === "id" ? "Mulai Belanja" : "Start Shopping"}</Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="my-orders">
          {orders.map((o) => (
            <Link key={o.id} to={`/order/${o.id}`} className="block bg-white rounded-2xl border border-border/60 p-5 hover:border-holy-red transition-all" data-testid={`order-${o.order_number}`}>
              <div className="flex justify-between flex-wrap gap-3">
                <div>
                  <div className="font-mono text-sm font-semibold text-holy-red">{o.order_number}</div>
                  <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString(lang === "id" ? "id-ID" : "en-US")}</div>
                  <div className="text-xs text-muted-foreground mt-1">{o.items.length} {lang === "id" ? "produk" : "items"} · {o.shipping_courier} {o.shipping_service}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-foreground">{formatMoney(o.total_idr, o.total_usd, currency)}</div>
                  <span className={`inline-block mt-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${statusColor(o.status)}`}>{o.status}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyAccount;
